import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { Button, Grid, useMediaQuery } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";

import InputFieldText from "Components/Common/InputFieldText";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import Backdrop from "Components/Common/Backdrop";
import { dateFormats } from "common/constant";
import DatePicker from "Components/Common/DatePicker";
import SelectField from "Components/Common/SelectField";
import Tooltip from "Components/Common/Tooltip";
import { billingSubscriptionTypes, communicationOption } from "common/enums";

import { useLazyDispatchedBillReportQuery } from "apis/Billing";
import { useLazyDownloadExcelQuery } from "slices/api";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { isAuthorizedUser } from "utils";

const RECORDS_PER_PAGE = 10;

const initialQuery = {
  meterNumber: "",
  consumerNumber: "",
  dateTime: moment(),
};

const DispatchedBill = ({ t }) => {
  const { roles } = useSelector((state) => state.Login);

  const [query, setQuery] = useState(initialQuery);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [communicationType, setcommunicationType] = useState("");

  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const [
    getDispatchedBillData,
    { data: { data: tableData } = {}, isLoading, isFetching },
  ] = useLazyDispatchedBillReportQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const sm = useMediaQuery("(max-width: 599px)");

  const downloadDispatchedBillReport = async () => {
    try {
      await downloadExcel({
        endpoint: "/report/dispatched-bill-report-excel",
        sheetName: "Dispatched Bills",
        query: {
          subscriptionType,
          communicationType,
          dateTime: encodeURIComponent(query.dateTime.format("YYYY-MM-DD")),
        },
      });
    } catch (error) {}
  };

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const tableColumns = useMemo(() => {
    const columns = [];
    if (tableData?.[0]) {
      const filteredKeys = Object.keys(tableData?.[0]).filter(
        (key) => key !== "updatedAt"
      );
      filteredKeys.forEach((key) => {
        columns.push(
          key === "dispatchedDateTime"
            ? {
                title: convertCamelTextToCapitalizeText(key),
                renderCell: (item) => {
                  return (
                    <span className="d-flex align-items-center gap-1">
                      {item?.dispatchedDateTime}
                      {item?.updatedAt ? (
                        <Tooltip title={`Last Update : ${t(item?.updatedAt)}`}>
                          <InfoOutlinedIcon
                            className="ms-1 cursor-pointer"
                            color="primary"
                            fontSize="small"
                          />
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                    </span>
                  );
                },
                columnProps: {
                  style: {
                    width: "9%",
                  },
                },
              }
            : {
                title: convertCamelTextToCapitalizeText(key),
                field: key,
              }
        );
      });
    }
    return columns;
  }, [tableData]);

  const updatedTableData = useMemo(() => {
    if (tableData) {
      return tableData.map((row) =>
        Object.entries(row).reduce((res, [key, value]) => {
          if (
            typeof value === "string" &&
            moment(value, dateFormats, true).isValid()
          ) {
            return {
              ...res,
              [key]: convertUTCDateToLocalDate(value).format(
                "DD-MM-YYYY HH:mm:ss A"
              ),
            };
          }
          return { ...res, [key]: value };
        }, {})
      );
    }
  }, [tableData]);

  const getDispatchedBill = async (query) => {
    try {
      await getDispatchedBillData({
        ...query,
        subscriptionType,
        communicationType,
        dateTime: encodeURIComponent(query.dateTime.format("YYYY-MM-DD")),
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (subscriptionType) {
      getDispatchedBill({ ...initialQuery, subscriptionType });
    }
  }, [subscriptionType]);

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  useEffect(() => {
    if (communicationOption.length) {
      setcommunicationType(communicationOption[0].value);
    }
  }, [communicationOption]);

  return (
    <>
      <Backdrop open={isLoading || isFetching || isExcelFileDownloading} />
      <BreadCrumb title={t("Dispatched Bills")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getDispatchedBill({ ...query, subscriptionType });
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Communication Type") + "*"}
              onChange={(_, { value }) => setcommunicationType(value)}
              value={
                communicationOption.find(
                  ({ value }) => value == communicationType
                ) ?? ""
              }
              options={communicationOption}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t("Select Month and Year")}
              className="w-100"
              views={["month", "year"]}
              value={query.dateTime}
              disableFuture
              onChange={(date) => {
                setQuery((prev) => ({
                  ...prev,
                  dateTime: date,
                }));
              }}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              onChange={(e) => {
                setQuery((prev) => ({
                  ...prev,
                  consumerNumber: e.target.value,
                }));
              }}
              value={query.consumerNumber}
              placeholder={t("Enter consumer number")}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              onChange={(e) => {
                setQuery((prev) => ({ ...prev, meterNumber: e.target.value }));
              }}
              value={query.meterNumber}
              placeholder={t("Enter meter number")}
            />
          </Grid>

          <Grid item lg={5}>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                getDispatchedBill({ ...initialQuery, subscriptionType });
                setQuery(initialQuery);
                handleResetPage();
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={downloadDispatchedBillReport}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={tableColumns}
        data={updatedTableData}
        pageSize={RECORDS_PER_PAGE}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo={!!tableColumns?.length}
        maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 250px)"}
      />
    </>
  );
};

DispatchedBill.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(DispatchedBill);
